import { Button, Group, Modal } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { fieldErrors } from '../api/errors';
import { listKey } from '../api/queryKeys';
import { notifyError, notifySaved } from '../lib/notify';
import { buildBody, type FieldDef, FormFields, seedFormValues, validateFields } from './CrudSection';

/**
 * Changing one record, on the page that record is the subject of.
 *
 * `CrudSection` answers "a collection, and a dialog to add to it". A record screen is the other
 * half — one subject with its history gathered underneath — and until this existed the only way to
 * change the subject from its own page was to leave it, find it again in a list, and press Edit
 * there. That is the arrangement the staff pages already rejected once, for a concrete reason: while
 * a person could be edited from both places, the same phone number had two homes and no way to tell
 * which one a manager had used.
 *
 * The form itself is `CrudSection`'s, field for field, so a page and its list cannot disagree about
 * what a record is or about which of its numbers are stored in minor units.
 */
export interface RecordEditModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  fields: FieldDef[];
  /** The record as it is stored. Values are converted for editing on the way in. */
  record: Record<string, unknown>;
  /** Where the change goes. */
  path: string;
  method?: 'PATCH' | 'PUT';
  /** Query paths to refetch once it lands — the list the subject came from, at least. */
  invalidate: string[];
  /** What to call it in the "Saved" line. */
  savedLabel: string;
}

/**
 * Mounted only while it is open, so the boxes are seeded from the record ONCE and from what is
 * stored NOW.
 *
 * A record screen renders this next to the subject it is about, and that subject arrives from a
 * query that refetches. Seeding on every render would throw away what the manager is typing the
 * moment anything invalidates; seeding at first mount and leaving it would open the form on the
 * empty object the page had before its query landed, and save blanks over the lot.
 */
export function RecordEditModal(props: RecordEditModalProps) {
  if (!props.opened) return null;
  return <EditForm {...props} />;
}

function EditForm(props: RecordEditModalProps) {
  const queryClient = useQueryClient();
  const form = useForm<Record<string, unknown>>({
    initialValues: seedFormValues(props.fields, props.record),
    validate: (values) => validateFields(props.fields, values, true),
  });

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      rawApi(props.method ?? 'PATCH', props.path, buildBody(values, props.fields, true)),
    onSuccess: () => {
      for (const path of props.invalidate) {
        void queryClient.invalidateQueries({ queryKey: listKey(path) });
      }
      props.onClose();
      notifySaved(props.savedLabel);
    },
    onError: (err: Error) => {
      // Field-level problems belong on the fields; only the summary goes to the toast.
      const byField = fieldErrors(err);
      if (Object.keys(byField).length) form.setErrors(byField);
      notifyError(err, 'save these details');
    },
  });

  return (
    <Modal
      opened
      onClose={props.onClose}
      title={props.title}
      size="lg"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <FormFields form={form} fields={props.fields} editing />
        <Group justify="flex-end" mt="xl">
          <Button variant="default" onClick={props.onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save changes
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
